"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import { apiClient } from "@/lib/api/client"
import { apiUrl } from "@/lib/api/url"
import { crmKeys } from "@/lib/apps/autoleads/keys/crm"
import { leadKeys } from "@/lib/apps/autoleads/keys/lead"
import { outreachKeys } from "@/lib/apps/autoleads/keys/outreach"
import { taskBoardKeys } from "@/lib/apps/autoleads/keys/task-board"
import { teamDashboardKeys } from "@/lib/apps/autoleads/keys/team-dashboard"

import type {
  CrmAccountDisqualifyReason,
  CrmAccountStatus,
  CrmActivityType,
  CrmAssignmentRule,
  CrmChainStepInput,
  CrmFieldValue,
  CrmPlaybook,
  CrmProduct,
  CrmProductInput,
  CrmRevenueLineInput,
  CrmTaskType,
  CrmTimelineEntry,
  CrmWorkRow,
  CrmWorkSource,
} from "@/nango/types/apps.autoleads/autoleads/Crm"
import type { TaskCheckOutcome } from "@/nango/types/apps.autoleads/autoleads/TaskBoard"

type Method = "POST" | "PATCH" | "DELETE"
type Request = { path: string; method?: Method; data?: object }
type Options = { successMessage?: string; onSuccess?: () => void }

// Every CRM write shares one contract: send, then refresh everything that
// counts the same work. A campaign step closed from "my tasks" also moves that
// campaign's board, so its caches go too.
function useCrmWrite<TVars, TResult = unknown>(
  toRequest: (vars: TVars) => Request,
  options: Options = {},
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: TVars) => {
      const { path, method = "POST", data } = toRequest(vars)
      return apiClient<TResult>({ url: apiUrl(path), method, data })
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: crmKeys.all })
      void qc.invalidateQueries({ queryKey: taskBoardKeys.all })
      void qc.invalidateQueries({ queryKey: outreachKeys.all })
      void qc.invalidateQueries({ queryKey: leadKeys.all })
      if (options.successMessage) toast.success(options.successMessage)
      options.onSuccess?.()
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : String(error))
    },
  })
}

// --- revenue ----------------------------------------------------------------

// A company's worth also feeds the team dashboard's revenue tiles.
function useRevenueWrite<TVars>(
  toRequest: (vars: TVars) => Request,
  options: Options = {},
) {
  const qc = useQueryClient()
  return useCrmWrite<TVars>(toRequest, {
    ...options,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: teamDashboardKeys.all })
      options.onSuccess?.()
    },
  })
}

export function useCreateRevenueLine(options?: Options) {
  return useRevenueWrite<{ accountId: number; line: CrmRevenueLineInput }>(
    ({ accountId, line }) => ({
      path: "api/al/crm_revenue_line/",
      data: { account_id: accountId, ...line },
    }),
    options,
  )
}

export function useUpdateRevenueLine(options?: Options) {
  return useRevenueWrite<{ id: number; line: CrmRevenueLineInput }>(
    ({ id, line }) => ({
      path: `api/al/crm_revenue_line/${id}/`,
      method: "PATCH",
      data: line,
    }),
    options,
  )
}

export function useDeleteRevenueLine(options?: Options) {
  return useRevenueWrite<number>(
    (id) => ({ path: `api/al/crm_revenue_line/${id}/`, method: "DELETE" }),
    options,
  )
}

// --- accounts ---------------------------------------------------------------

export function useClaimAccount(options?: Options) {
  return useCrmWrite<number>(
    (id) => ({ path: `api/al/crm_account/${id}/claim/` }),
    options,
  )
}

export function useAssignAccount(options?: Options) {
  return useCrmWrite<{ accountId: number; userId: number | null }>(
    ({ accountId, userId }) => ({
      path: `api/al/crm_account/${accountId}/assign/`,
      data: { user_id: userId },
    }),
    options,
  )
}

export function useRenameAccount(options?: Options) {
  return useCrmWrite<{ accountId: number; name: string }>(
    ({ accountId, name }) => ({
      path: `api/al/crm_account/${accountId}/rename/`,
      data: { name },
    }),
    options,
  )
}

export function useSetAccountStatus(options?: Options) {
  return useCrmWrite<{
    accountId: number
    status: CrmAccountStatus
    reason?: CrmAccountDisqualifyReason
    note?: string
  }>(
    ({ accountId, status, reason, note }) => ({
      path: `api/al/crm_account/${accountId}/set_status/`,
      data: { status, reason, note },
    }),
    options,
  )
}

export function useSetPrimary(options?: Options) {
  return useCrmWrite<{ accountId: number; leadId: number }>(
    ({ accountId, leadId }) => ({
      path: `api/al/crm_account/${accountId}/set_primary/`,
      data: { lead_id: leadId },
    }),
    options,
  )
}

export function useSaveCrmSettings(options?: Options) {
  return useCrmWrite<{ team: number; oneSequencePerOffer: boolean }>(
    ({ team, oneSequencePerOffer }) => ({
      path: "api/al/crm_account/settings/",
      data: { team, one_sequence_per_offer: oneSequencePerOffer },
    }),
    options,
  )
}

export function useMergeAccounts(options?: Options) {
  return useCrmWrite<
    { sourceId: number; intoId: number },
    { merge_id: number; account_id: number }
  >(
    ({ sourceId, intoId }) => ({
      path: "api/al/crm_account/merge/",
      data: { source_id: sourceId, into_id: intoId },
    }),
    options,
  )
}

export function useUnmergeAccount(options?: Options) {
  return useCrmWrite<number>(
    (mergeId) => ({
      path: "api/al/crm_account/unmerge/",
      data: { merge_id: mergeId },
    }),
    options,
  )
}

export function useDismissDuplicate(options?: Options) {
  return useCrmWrite<{ a: number; b: number }>(
    (pair) => ({ path: "api/al/crm_account/dismiss_duplicate/", data: pair }),
    options,
  )
}

// --- Meetings from the note-taking tool ---------------------------------------

// Sets a meeting aside (internal, or about no client) so the inbox stops
// offering it; ``restore`` brings it back, for the toast's undo.
export function useDismissMeeting(options?: Options) {
  return useCrmWrite<{ meetingId: number }>(
    ({ meetingId }) => ({
      path: `api/al/meeting_record/${meetingId}/dismiss/`,
    }),
    options,
  )
}

export function useRestoreMeeting(options?: Options) {
  return useCrmWrite<{ meetingId: number }>(
    ({ meetingId }) => ({
      path: `api/al/meeting_record/${meetingId}/restore/`,
    }),
    options,
  )
}

export function useAttachMeeting(options?: Options) {
  return useCrmWrite<{ meetingId: number; accountId: number }>(
    ({ meetingId, accountId }) => ({
      path: `api/al/meeting_record/${meetingId}/attach/`,
      data: { account_id: accountId },
    }),
    options,
  )
}

// --- CRM tasks --------------------------------------------------------------

export type CreateTasksInput = {
  accountId: number
  leadId: number | null
  personKey: string
  startAt: string | null
  steps: CrmChainStepInput[]
}

export function useCreateTasks(options?: Options) {
  return useCrmWrite<CreateTasksInput, { tasks: CrmWorkRow[] }>(
    (input) => ({
      path: "api/al/crm_task/",
      data: {
        account_id: input.accountId,
        lead_id: input.leadId,
        person_key: input.personKey,
        start_at: input.startAt,
        steps: input.steps,
      },
    }),
    options,
  )
}

export function useUpdateTask(options?: Options) {
  return useCrmWrite<{
    id: number
    title?: string
    description?: string
    due_at?: string
    task_type_id?: number
  }>(
    ({ id, ...data }) => ({
      path: `api/al/crm_task/${id}/`,
      method: "PATCH",
      data,
    }),
    options,
  )
}

export type CrmTaskAction = "complete" | "cancel" | "reopen" | "cancel_chain"

export function useCrmTaskAction(options?: Options) {
  return useCrmWrite<{ id: number; action: CrmTaskAction }>(
    ({ id, action }) => ({ path: `api/al/crm_task/${id}/${action}/` }),
    options,
  )
}

export function useSaveChainAsPlaybook(options?: Options) {
  return useCrmWrite<{ taskId: number; name: string }, CrmPlaybook>(
    ({ taskId, name }) => ({
      path: `api/al/crm_task/${taskId}/save_as_playbook/`,
      data: { name },
    }),
    options,
  )
}

// --- activity log -------------------------------------------------------------

export type LogActivityInput = {
  accountId: number
  activityTypeId: number
  body: string
  data: Record<string, CrmFieldValue>
  leadId: number | null
  personKey: string
  occurredAt: string | null
}

export function useLogActivity(options?: Options) {
  return useCrmWrite<
    LogActivityInput,
    { activity: CrmTimelineEntry; followups: CrmWorkRow[] }
  >(
    (input) => ({
      path: "api/al/crm_activity/",
      data: {
        account_id: input.accountId,
        activity_type_id: input.activityTypeId,
        body: input.body,
        data: input.data,
        lead_id: input.leadId,
        person_key: input.personKey,
        occurred_at: input.occurredAt,
      },
    }),
    options,
  )
}

export function useDeleteActivity(options?: Options) {
  return useCrmWrite<number>(
    (id) => ({ path: `api/al/crm_activity/${id}/`, method: "DELETE" }),
    options,
  )
}

// --- any task, whatever its source -------------------------------------------

export function useAssignWork(options?: Options) {
  return useCrmWrite<{
    team: number
    source: CrmWorkSource
    id: number
    userId: number | null
  }>(
    ({ team, source, id, userId }) => ({
      path: "api/al/crm_inbox/assign/",
      data: { team, source, id, user_id: userId },
    }),
    options,
  )
}

// Campaign work is closed through the campaign's own endpoints, so the ladder
// logic (opening the next step, freeing the company) runs exactly as on the
// board.
export function useCompleteSequenceStep(options?: Options) {
  return useCrmWrite<number>(
    (taskId) => ({
      path: "api/al/outreach_task/complete/",
      data: { task_id: taskId },
    }),
    options,
  )
}

// Drops the contact's file on its upload step, which completes it.
export function useAttachSequenceStepFile(options?: Options) {
  return useCrmWrite<{ taskId: number; fileId: string }>(
    ({ taskId, fileId }) => ({
      path: "api/al/outreach_task/attach/",
      data: { task_id: taskId, file_id: fileId },
    }),
    options,
  )
}

export function useSkipSequenceStep(options?: Options) {
  return useCrmWrite<number>(
    (taskId) => ({
      path: "api/al/outreach_task/skip/",
      data: { task_id: taskId },
    }),
    options,
  )
}

export function useCheckSequenceStep(options?: Options) {
  return useCrmWrite<{ taskId: number; outcome: TaskCheckOutcome }>(
    ({ taskId, outcome }) => ({
      path: "api/al/outreach_task/check/",
      data: { task_id: taskId, outcome },
    }),
    options,
  )
}

export function useCompleteCampaignNote(options?: Options) {
  return useCrmWrite<number>(
    (noteId) => ({ path: `api/al/outreach_note/${noteId}/complete/` }),
    options,
  )
}

// --- team settings ------------------------------------------------------------

type SettingsKind =
  | "crm_task_type"
  | "crm_activity_type"
  | "crm_playbook"
  | "crm_assignment_rule"
  | "crm_product"

function useSaveSetting<TPayload extends object, TResult>(
  kind: SettingsKind,
  options?: Options,
) {
  return useCrmWrite<
    { team: number; id: number | null; payload: TPayload },
    TResult
  >(
    ({ team, id, payload }) =>
      id === null
        ? { path: `api/al/${kind}/`, data: { team, ...payload } }
        : {
            path: `api/al/${kind}/${id}/?team=${team}`,
            method: "PATCH",
            data: payload,
          },
    options,
  )
}

export function useDeleteSetting(kind: SettingsKind, options?: Options) {
  return useCrmWrite<{ team: number; id: number }>(
    ({ team, id }) => ({
      path: `api/al/${kind}/${id}/?team=${team}`,
      method: "DELETE",
    }),
    options,
  )
}

export function useReorderSetting(kind: SettingsKind, options?: Options) {
  return useCrmWrite<{ team: number; ids: number[] }>(
    ({ team, ids }) => ({
      path: `api/al/${kind}/reorder/`,
      data: { team, ids },
    }),
    options,
  )
}

export type TaskTypeInput = Pick<
  CrmTaskType,
  "label" | "icon" | "color" | "default_delay_days"
> & {
  archived?: boolean
}
export function useSaveTaskType(options?: Options) {
  return useSaveSetting<TaskTypeInput, CrmTaskType>("crm_task_type", options)
}

export type ActivityTypeInput = Omit<
  CrmActivityType,
  "id" | "position" | "archived"
> & { archived?: boolean }
export function useSaveActivityType(options?: Options) {
  return useSaveSetting<ActivityTypeInput, CrmActivityType>(
    "crm_activity_type",
    options,
  )
}

export function useSaveProduct(options?: Options) {
  return useSaveSetting<CrmProductInput, CrmProduct>("crm_product", options)
}

export type PlaybookInput = {
  name: string
  description: string
  archived?: boolean
  steps: CrmChainStepInput[]
}
export function useSavePlaybook(options?: Options) {
  return useSaveSetting<PlaybookInput, CrmPlaybook>("crm_playbook", options)
}

export function useLaunchPlaybook(options?: Options) {
  return useCrmWrite<{
    playbookId: number
    accountId: number
    leadId: number | null
    startAt: string | null
  }>(
    ({ playbookId, accountId, leadId, startAt }) => ({
      path: `api/al/crm_playbook/${playbookId}/launch/`,
      data: { account_id: accountId, lead_id: leadId, start_at: startAt },
    }),
    options,
  )
}

export type RuleInput = Omit<CrmAssignmentRule, "id" | "position" | "cursor">
export function useSaveRule(options?: Options) {
  return useSaveSetting<RuleInput, CrmAssignmentRule>(
    "crm_assignment_rule",
    options,
  )
}
